import { KEGatewayClient } from '../KEGatewayClient';
import { ErrorBus } from '~/features/error-bus';
import type {
  IProductAction,
  PromotionResult,
  ProductListItem,
  IOffer,
  getProductCompilationsResponse,
  GetManyResponse,
  ProductIdMap,
  GetProductResponse,
  GetProductActionResponse,
  PostWishSaleResponse,
  GetBoughtWithResponse,
  GetReviewsResponse,
  GetOptionInfoResponse,
} from './models';
import type { IProductOptionInfo } from '~/pages/index/-src/shared/types/product';

function error(e: any) {
  ErrorBus.emitError(e);
}

export class ProductService extends KEGatewayClient {
  public async getMany(ids: number[]): Promise<ProductIdMap> {
    try {
      const response = await this.get<GetManyResponse>('product/', {
        searchParams: { ids: String(ids) },
      });
      return response.payload;
    } catch (e) {
      error(e);

      return {};
    }
  }

  public async getProduct(id: number): Promise<PromotionResult<ProductListItem> | null> {
    try {
      const response = await this.get<GetProductResponse>('v2/product/' + id);
      return response.payload;
    } catch (e) {
      error(e);

      return null;
    }
  }

  public async getActionController(productId: number): Promise<IProductAction[] | null> {
    try {
      const response = await this.get<GetProductActionResponse>('product/actions/' + productId);
      return response.payload;
    } catch (e) {
      error(e);

      return null;
    }
  }

  public async postWishSale(
    url: string,
    productId: number
  ): Promise<PromotionResult<ProductListItem> | null> {
    try {
      const product = productId ? `name=${productId}` : 'name=${productId}`';

      const response = await this.post<PostWishSaleResponse, unknown>(
        'product/wish-sale/' + url + '?${product}',
        { productId }
      );
      return response.payload;
    } catch (e) {
      error(e);

      return null;
    }
  }

  public async getBoughtWith(query: string, size?: number): Promise<ProductListItem[] | null> {
    try {
      const sizeValue = size ? `&size=${size}` : '';

      const response = await this.get<GetBoughtWithResponse>(
        'v2/product/bought-with?productIds=' + query + sizeValue
      );
      return response.payload;
    } catch (e) {
      error(e);

      return null;
    }
  }

  public async getSimilar(productId: string | number, size?: number): Promise<ProductListItem[]> {
    try {
      const sizeValue = size ? `?size=${size}` : '';

      const response = await this.get<ProductListItem[]>(
        'v2/product/' + productId + '/similar' + sizeValue
      );

      return response;
    } catch (e) {
      error(e);

      return [];
    }
  }

  public async getReviews(
    productId: number,
    amount = 5,
    page = 0,
    hasPhoto = false
  ): Promise<ProductListItem[] | null> {
    // if (process.env.IS_PRERENDER) {
    //   return Promise.resolve(null);
    // }

    try {
      const response = await this.get<GetReviewsResponse>(
        'product/' + productId + '/reviews' + `?amount=${amount}&page=${page}&hasPhoto=${hasPhoto}`
      );
      return response.payload;
    } catch (e) {
      error(e);

      return null;
    }
  }

  public async getProductCompilations(page = 0, size = 10): Promise<IOffer[]> {
    const searchParams = {
      page,
      size,
    };

    try {
      const response = await this.get<getProductCompilationsResponse>('product/compilations', {
        searchParams,
      });

      if (!response) {
        return [];
      }

      return response.payload;
    } catch (e) {
      error(e);

      return [];
    }
  }

  public async getOptionInfo(
    optionIds: number[],
    skuId: number[]
  ): Promise<PromotionResult<IProductOptionInfo[]> | null> {
    try {
      const response = await this.get<GetOptionInfoResponse>('product/option', {
        searchParams: {
          optionIds: String(optionIds),
          skuId: String(skuId),
        },
      });
      return response.payload;
    } catch (e) {
      error(e);

      return null;
    }
  }
}

export const productService = new ProductService();
